<template>
  <div class="drawer-root">
    <div class="drawer-scrim" @click="$emit('close')"></div>
    <aside class="drawer" :class="{ done: form.status === 'done' }" role="dialog" aria-modal="true">
      <!-- Header -->
      <header class="drawer-head">
        <div class="dh-titles">
          <span class="dh-eyebrow">{{ t('drawer.details') }} · #{{ task.id }}</span>
          <h2 class="dh-title">{{ form.title || task.title }}</h2>
        </div>
        <button class="icon-btn" :title="t('drawer.close')" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>

      <!-- Tabs -->
      <nav class="drawer-tabs">
        <button class="dt-tab" :class="{ active: tab === 'details' }" @click="tab = 'details'">{{ t('drawer.tabDetails') }}</button>
        <button class="dt-tab" :class="{ active: tab === 'activity' }" @click="tab = 'activity'">{{ t('drawer.tabActivity') }}</button>
        <button class="dt-tab" :class="{ active: tab === 'time' }" @click="tab = 'time'">{{ t('drawer.tabTime') }}</button>
      </nav>

      <div class="drawer-body">
        <!-- TAB 1 · Details — header, description, urgency, status, project… -->
        <div v-show="tab === 'details'">
          <div class="form-group">
            <label>{{ t('taskForm.title') }} *</label>
            <input v-model="form.title" class="form-control" maxlength="200" />
          </div>

          <div class="form-group">
            <label>{{ t('taskForm.description') }}</label>
            <textarea v-model="form.description" class="form-control" rows="3"></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ t('taskForm.urgency') }}</label>
              <select v-model="form.urgency" class="form-control">
                <option value="low">{{ t('urgency.low') }}</option>
                <option value="normal">{{ t('urgency.normal') }}</option>
                <option value="high">{{ t('urgency.high') }}</option>
                <option value="critical">{{ t('urgency.critical') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ t('taskForm.status') }}</label>
              <select v-model="form.status" class="form-control">
                <option value="open">{{ t('status.open') }}</option>
                <option value="in_progress">{{ t('status.in_progress') }}</option>
                <option value="done">{{ t('status.done') }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ t('taskForm.project') }}</label>
              <select v-model="form.project_id" class="form-control" @change="autoSave({ project_id: form.project_id || null })">
                <option :value="null">{{ t('common.none') }}</option>
                <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ t('taskForm.dueDate') }}</label>
              <input v-model="form.due_at" type="date" class="form-control" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ t('taskForm.visibility') }}</label>
              <select v-model="form.visibility" class="form-control" :disabled="lockedPrivate">
                <option value="shared">{{ t('projects.sharedBadge') }}</option>
                <option value="private">{{ t('projects.privateBadge') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ t('taskForm.assignTo') }}</label>
              <select v-model="form.assigned_to" class="form-control" @change="autoSave({ assigned_to: form.assigned_to || null })">
                <option :value="null">{{ t('taskForm.unassigned') }}</option>
                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
          </div>

          <p v-if="error" class="form-error">{{ error }}</p>
          <div class="save-row">
            <button class="btn btn-primary btn-sm" :disabled="saving" @click="save">
              {{ saving ? t('drawer.saving') : t('drawer.save') }}
            </button>
          </div>
        </div>

        <!-- TAB 2 · Subtasks & comments -->
        <div v-show="tab === 'activity'">
          <section class="block flush">
            <TaskSubtasks :task-id="task.id" />
          </section>
          <section class="block">
            <TaskComments :task-id="task.id" />
          </section>
        </div>

        <!-- TAB 3 · Time log — timer + editable session log -->
        <div v-show="tab === 'time'">
          <section class="block flush">
            <div class="block-head">
              <h3 class="block-title">{{ t('sessions.title') }}</h3>
              <div class="block-tools">
                <TaskTimer
                  :task-id="task.id"
                  :timer-seconds="task.timer_seconds"
                  :timer-started-at="task.timer_started_at"
                  @update="onTimerUpdate"
                />
                <span class="sess-total">{{ t('sessions.total') }}: {{ totalLabel }}</span>
              </div>
            </div>

            <div class="sess-add">
              <input v-model="newSession.date" type="date" class="form-control sess-date" />
              <input v-model.number="newSession.minutes" type="number" min="0" step="1" class="form-control sess-min" :placeholder="t('sessions.minutes')" />
              <button class="btn btn-ghost btn-sm" :disabled="!canAdd" @click="addSession">{{ t('sessions.add') }}</button>
            </div>

            <p v-if="!sessions.length" class="muted-sm">{{ t('sessions.empty') }}</p>
            <ul v-else class="sess-list">
              <li v-for="s in sessions" :key="s.id" class="sess-row" :class="{ running: s.running }">
                <template v-if="s.running">
                  <span class="sess-cell sess-date-cell">{{ fmtDate(s.started_at) }}</span>
                  <span class="sess-cell sess-live">{{ t('sessions.running') }}</span>
                  <span class="sess-cell sess-src">{{ t('sessions.timer') }}</span>
                  <span class="sess-cell sess-actions"></span>
                </template>
                <template v-else>
                  <input class="sess-cell sess-date-cell edit" type="date" :value="dateOnly(s.started_at)" @change="editDate(s, $event)" />
                  <input class="sess-cell sess-min-cell edit" type="number" min="0" step="1" :value="s.minutes" @change="editMinutes(s, $event)" />
                  <span class="sess-cell sess-src">{{ s.source === 'manual' ? t('sessions.manual') : t('sessions.timer') }}</span>
                  <span class="sess-cell sess-actions">
                    <button class="icon-btn sm danger" :title="t('subtasks.removeRow')" @click="removeSession(s)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    </button>
                  </span>
                </template>
              </li>
            </ul>
          </section>
        </div>
      </div>

      <!-- Footer actions -->
      <footer class="drawer-foot">
        <button v-if="task.archived_at" class="btn btn-ghost" @click="$emit('restore', task)">
          {{ t('tasks.restore') }}
        </button>
        <template v-else>
          <button v-if="task.status === 'done'" class="btn btn-ghost" @click="$emit('reopen', task)">
            {{ t('taskCard.reopen') }}
          </button>
          <button class="btn btn-primary" :title="t('tasks.completeArchives')" @click="$emit('complete', task)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="20 6 9 17 4 12"/></svg>
            {{ t('taskCard.markDone') }}
          </button>
        </template>
      </footer>
    </aside>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import api from '@/api'
import TaskTimer from '@/components/TaskTimer.vue'
import TaskSubtasks from '@/components/TaskSubtasks.vue'
import TaskComments from '@/components/TaskComments.vue'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'

function toLocalDate(str) {
  if (!str) return ''
  const d = new Date(str)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'TaskDetailDrawer',
  components: { TaskTimer, TaskSubtasks, TaskComments },
  props: {
    task:     { type: Object, required: true },
    projects: { type: Array,  default: () => [] },
  },
  emits: ['close', 'saved', 'changed', 'complete', 'reopen', 'restore'],
  setup(props, { emit }) {
    const { t, locale } = useI18n()
    const toast = useToast()
    const saving = ref(false)
    const error  = ref('')
    const users  = ref([])
    const tab    = ref('details')   // details | activity | time

    const form = reactive({
      title:       props.task.title       || '',
      description: props.task.description || '',
      urgency:     props.task.urgency     || 'normal',
      status:      props.task.status      || 'open',
      visibility:  props.task.visibility  || 'shared',
      project_id:  props.task.project_id  ?? null,
      assigned_to: props.task.assigned_to ?? null,
      due_at:      toLocalDate(props.task.due_at),
    })

    const lockedPrivate = computed(() => {
      const p = props.projects.find(p => p.id === form.project_id)
      return p?.visibility === 'private'
    })
    watch(lockedPrivate, (locked) => { if (locked) form.visibility = 'private' }, { immediate: true })

    // ── time sessions ──
    const sessions   = ref([])
    const newSession = reactive({ date: toLocalDate(new Date().toISOString()), minutes: null })
    const canAdd     = computed(() => Number(newSession.minutes) > 0)
    const totalMinutes = computed(() => sessions.value.reduce((s, x) => s + (x.running ? 0 : x.minutes), 0))
    const totalLabel = computed(() => {
      const h = Math.floor(totalMinutes.value / 60)
      const m = totalMinutes.value % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    })

    async function loadSessions() {
      try { sessions.value = (await api.getSessions(props.task.id)).data || [] } catch {}
    }
    async function addSession() {
      if (!canAdd.value) return
      try {
        const res = await api.addSession(props.task.id, { date: newSession.date, minutes: Number(newSession.minutes) })
        sessions.value = res.data || []
        newSession.minutes = null
        emit('changed')
      } catch (e) { error.value = e.message }
    }
    async function editMinutes(s, ev) {
      const minutes = Math.max(0, parseInt(ev.target.value, 10) || 0)
      try { sessions.value = (await api.updateSession(props.task.id, s.id, { minutes })).data || []; emit('changed') } catch {}
    }
    async function editDate(s, ev) {
      const date = ev.target.value
      if (!date) return
      try { sessions.value = (await api.updateSession(props.task.id, s.id, { date })).data || []; emit('changed') } catch {}
    }
    async function removeSession(s) {
      if (!confirm(t('sessions.deleteConfirm'))) return
      try { sessions.value = (await api.deleteSession(props.task.id, s.id)).data || []; emit('changed') } catch {}
    }
    function onTimerUpdate() { loadSessions(); emit('changed') }

    // Project + assignee apply immediately (no Save click). Keeps the drawer open
    // and asks the parent to reload the list so the task regroups under its project.
    async function autoSave(partial) {
      error.value = ''
      try {
        await api.updateTask(props.task.id, partial)
        emit('changed')
        toast.success(t('toast.saved'))
      } catch {
        error.value = t('toast.saveFailed')
        toast.error(t('toast.saveFailed'))
      }
    }

    async function save() {
      error.value = ''
      saving.value = true
      try {
        const payload = {
          title:       form.title,
          description: form.description || null,
          urgency:     form.urgency,
          status:      form.status,
          visibility:  form.visibility,
          project_id:  form.project_id  || null,
          assigned_to: form.assigned_to || null,
          due_at:      form.due_at      || null,
        }
        const res = await api.updateTask(props.task.id, payload)
        emit('saved', res.data)
        toast.success(t('toast.saved'))
      } catch {
        error.value = t('toast.saveFailed')
        toast.error(t('toast.saveFailed'))
      } finally {
        saving.value = false
      }
    }

    function dateOnly(dt) { return dt ? dt.slice(0, 10) : '' }
    function fmtDate(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt).toLocaleDateString(loc, { day: '2-digit', month: 'short' })
    }

    onMounted(async () => {
      loadSessions()
      try { users.value = (await api.getUsers()).data || [] } catch {}
    })

    return {
      t, tab, form, users, saving, error, lockedPrivate, save, autoSave,
      sessions, newSession, canAdd, totalLabel, addSession, editMinutes, editDate, removeSession, onTimerUpdate,
      dateOnly, fmtDate,
    }
  },
}
</script>

<style scoped>
.drawer-root { position: fixed; inset: 0; z-index: 1100; }
.drawer-scrim { position: absolute; inset: 0; background: rgba(16,22,40,.42); }

/* Floating, fully-rounded panel — soft edges, doesn't touch the screen edges. */
.drawer {
  position: absolute; inset-block: 14px; inset-inline-end: 14px;
  width: min(720px, calc(100% - 28px));
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(16,22,40,.22);
  display: flex; flex-direction: column;
  animation: drawer-in .2s ease;
}
@keyframes drawer-in { from { transform: translateY(8px); opacity: .4; } to { transform: none; opacity: 1; } }
.drawer.done { opacity: .96; }

.drawer-head {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 18px 20px; border-bottom: 1px solid var(--border);
}
.dh-titles { flex: 1; min-width: 0; }
.dh-eyebrow { font-family: var(--font-mono); font-size: 11px; color: var(--faint); letter-spacing: .03em; }
.dh-title { font-size: var(--fs-h4); font-weight: 700; margin-top: 3px; line-height: 1.3; }

/* Tab bar — sits between the header and the scrollable body. */
.drawer-tabs {
  display: flex; gap: 4px;
  padding: 8px 14px 0;
  border-bottom: 1px solid var(--border);
}
.dt-tab {
  border: none; background: none; cursor: pointer;
  padding: 9px 14px; margin-bottom: -1px;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  color: var(--muted);
  border-bottom: 2px solid transparent;
  transition: color .12s, border-color .12s;
  white-space: nowrap;
}
.dt-tab:hover { color: var(--text); }
.dt-tab.active { color: var(--accent-ink); border-bottom-color: var(--accent); }

.drawer-body { flex: 1; overflow-y: auto; padding: 18px 20px; }
/* First block in a tab has no preceding content — drop its divider/top spacing. */
.block.flush { border-top: none; padding-top: 0; margin-top: 0; }

/* TaskSubtasks / TaskComments carry their own "attached panel" chrome (border +
   side margins) meant for the inline list view. Inside the drawer tab that reads
   as a cramped box-in-a-box — flatten them so they sit flush and use full width. */
.drawer :deep(.subtasks-panel),
.drawer :deep(.comments-block) {
  margin: 0; padding: 0; border: none; background: none; border-radius: 0;
}
/* Give the subtask grid breathing room now that the drawer is wider. */
.drawer :deep(.sub-table) { width: 100%; }
.drawer :deep(.col-title) { width: auto; }

.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-size: 13px; color: var(--muted); margin-bottom: 6px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

.form-error { color: var(--danger); font-size: 13px; margin: 4px 0 10px; }
.save-row { display: flex; justify-content: flex-end; margin-bottom: 8px; }

.block { border-top: 1px solid var(--border); padding-top: 16px; margin-top: 18px; }
.block-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
.block-title { font-size: 14px; font-weight: 700; }
.block-tools { display: flex; align-items: center; gap: 12px; }
.sess-total { font-family: var(--font-mono); font-size: 12px; color: var(--muted); }

.sess-add { display: flex; gap: 8px; margin-bottom: 12px; }
.sess-date { flex: 1; }
.sess-min { width: 92px; }

.muted-sm { font-size: 12px; color: var(--faint); }
.sess-list { list-style: none; display: flex; flex-direction: column; gap: 4px; }
.sess-row {
  display: grid; grid-template-columns: 1fr 92px auto 34px; align-items: center; gap: 8px;
  padding: 5px 8px; border: 1px solid var(--border); border-radius: var(--radius-sm);
  background: var(--surface-2);
}
.sess-row.running { border-color: color-mix(in srgb, var(--accent) 40%, var(--border)); }
.sess-cell { font-size: 12.5px; min-width: 0; }
.sess-cell.edit { font-family: var(--font-mono); padding: 4px 6px; }
.sess-min-cell { text-align: end; font-variant-numeric: tabular-nums; }
.sess-src { font-size: 10.5px; color: var(--faint); text-align: center; }
.sess-live { color: var(--accent); font-weight: 600; }
.sess-actions { display: flex; justify-content: flex-end; }

.icon-btn {
  border: none; background: none; cursor: pointer; color: var(--muted);
  display: inline-flex; padding: 5px; border-radius: 8px;
}
.icon-btn:hover { color: var(--text); background: var(--hover); }
.icon-btn.sm { padding: 3px; }
.icon-btn.danger:hover { color: #fff; background: var(--danger); }

.drawer-foot {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px; border-top: 1px solid var(--border); background: var(--surface);
}

@media (max-width: 560px) {
  .form-row { grid-template-columns: 1fr; }
  .drawer { width: 100%; }
}
</style>
